import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { useGetTeams } from '@/modules/employees/hooks/useTeams';
import { ITeam } from '@/types/team';
import { keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { PaginationState } from '@tanstack/react-table';
import debounce from 'lodash.debounce';
import { useEffect, useMemo, useState } from 'react';
import { useLearningBudgetManagement } from '../../hooks/useLearningBudgetManagement';
import { EmployeesSearchParams } from '../../types';
import { DeleteTeamDialog } from '../DeleteTeamDialog';
import { ListTeamsTable } from '../ListTeamsTable';
import { TeamDialog } from '../TeamDialog';

export function ListTeamsTab() {
  const navigate = useNavigate();
  const userContext = useCurrentUserContext();
  const [selectedTeam, setSelectedTeam] = useState<ITeam | null>(null);
  const [teamToEdit, setTeamToEdit] = useState<ITeam | null>(null);

  const search = useSearch({
    strict: false,
  }) as EmployeesSearchParams;

  const [globalFilter, setGlobalFilter] = useState(() => search.name || '');

  const [paginationState, setPaginationState] = useState<PaginationState>(
    () => ({
      pageIndex: (search.page || 1) - 1,
      pageSize: search.limit || 10,
    }),
  );

  const {
    isLoading: isLoadingTeams,
    isRefetching: isRefetchingTeams,
    refetch: refetchTeams,
    data: { data: teams = [], metadata: { total = 0 } = {} } = {},
  } = useGetTeams(
    {
      page: paginationState.pageIndex + 1,
      limit: paginationState.pageSize,
      name: search.name,
    },
    {
      enabled: !!userContext?.employer?._id,
      placeholderData: keepPreviousData,
    },
  );

  const { showModal, Modal: LearningBudgetManagementModal } =
    useLearningBudgetManagement({
      afterUpdate: refetchTeams,
    });

  const debouncedSearchTerm = useMemo(() => {
    return debounce((term: string) => {
      navigate({
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          name: term?.trim() || undefined,
          page: 1,
        }),
      });
    }, 300);
  }, [navigate]);

  useEffect(() => {
    setPaginationState({
      pageIndex: (search.page || 1) - 1,
      pageSize: search.limit || 10,
    });
  }, [search.page, search.limit]);

  const onPaginationChange = ({ pageIndex, pageSize }: PaginationState) => {
    setPaginationState({ pageIndex, pageSize });
    navigate({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        page: pageIndex + 1,
        limit: pageSize,
      }),
    });
  };

  const onGlobalFilterChange = (value: string) => {
    setGlobalFilter(value);
    debouncedSearchTerm(value);
  };

  const onManageLearningBudgetFunds = (team: ITeam) => {
    showModal({
      variant: 'team',
      team,
    });
  };

  return (
    <>
      <header className="border-b px-8 pb-6">
        <h2 className="font-semibold text-mydra-black text-xl">Teams</h2>
        <p className="text-text mt-1">
          Create and manage teams for more efficient team management.
        </p>
      </header>

      <div className="flex-1 px-8 pt-6">
        <ListTeamsTable
          tableOptions={{
            data: teams,
            state: {
              isLoading: isLoadingTeams || isRefetchingTeams,
              pagination: paginationState,
              globalFilter: globalFilter,
            },
            rowCount: total,
            onPaginationChange: updater => {
              return typeof updater === 'function'
                ? onPaginationChange(updater(paginationState))
                : onPaginationChange(updater);
            },
            onGlobalFilterChange: updater => {
              return typeof updater === 'function'
                ? onGlobalFilterChange(updater(globalFilter))
                : onGlobalFilterChange(updater);
            },
          }}
          onDeleteTeam={setSelectedTeam}
          onManageLearningBudgetFunds={onManageLearningBudgetFunds}
          onEditTeam={setTeamToEdit}
        />
      </div>
      <DeleteTeamDialog
        open={!!selectedTeam}
        onClose={() => setSelectedTeam(null)}
        onSuccess={() => {
          setSelectedTeam(null);
        }}
        team={selectedTeam?._id ?? ''}
      />
      <TeamDialog
        open={!!teamToEdit}
        onClose={() => setTeamToEdit(null)}
        onSuccess={() => {
          setTeamToEdit(null);
          refetchTeams();
        }}
        team={teamToEdit || undefined}
      />
      <LearningBudgetManagementModal />
    </>
  );
}
